import { useMutation, useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/api/client";

// Wire-format mirrors src/flowstack/server/routes/affdo.py. P1.3a
// surfaces per-fragment + per-dihedral parameter-quality data from
// the AFFDO torsion-refinement output the platform's typed-object
// catalog already persists.

/** Three-state quality label the GUI uses to color a fragment row. */
export type FragmentStatus = "ok" | "partial" | "failed";

export interface FragmentSummary {
  torsion_refinement_result_id: string;
  fragment_id: string;
  torsion_set_id: string;
  n_fitted: number;
  n_failed: number;
  n_total: number;
  max_fit_rmsd_kcal_per_mol: number | null;
  mean_fit_rmsd_kcal_per_mol: number | null;
  status: FragmentStatus;
}

export interface FragmentSummaryListResponse {
  run_id: string;
  fragments: FragmentSummary[];
}

/**
 * AFFDO's own word for what happened to a torsion.
 *
 * Kept as a widened `string` on the wire because it is a PERSISTED
 * vocabulary: the server projects whatever the artifact recorded, and a
 * value added upstream must not make an already-deployed portal
 * unparseable. Use `isGaffRetained` rather than switching on it exhaustively.
 * `null` on a record written before the runner stamped it — unknown, not good.
 *
 * The band words AFFDO actually writes today are `high` / `medium` / `poor`
 * (it prints a NOTE for `medium`; `poor` falls back to standard GAFF2), but
 * the type stays widened on purpose — see above.
 * See `docs/source/dev/reference/torsion-fit-cascade.rst`.
 */
export type AffdoQuality = string | null;

/**
 * The three quality fields every per-torsion row carries.
 *
 * `fit_norm_rmse` / `gaff_norm_rmse` are AFFDO's **dimensionless** norm_RMSE
 * — the number every gate in the fit cascade actually reads. They are NOT
 * energies: never render them with a kcal/mol suffix. The kcal/mol number is
 * the separate `fit_rmsd_kcal_per_mol`, a DIFFERENT quantity and not a
 * rescaling of these. See
 * `docs/source/dev/reference/torsion-fit-cascade.rst`.
 *
 * `affdo_quality` is named for what it is (#882): the band AFFDO recorded.
 * On a `gaff_sufficient` torsion that is the PRE-fit gate's verdict about
 * plain GAFF2, not a judgement about a fit that never ran — read it beside
 * `fit_status`, never label it "fit quality" unconditionally.
 */
export interface TorsionQualityFields {
  affdo_quality: AffdoQuality;
  fit_norm_rmse: number | null;
  gaff_norm_rmse: number | null;
}

export interface FittedTorsionRow extends TorsionQualityFields {
  torsion_id: string;
  atoms_in_fragment: number[];
  fit_rmsd_kcal_per_mol: number;
  fit_method: string;
}

export interface FailedTorsionRow extends TorsionQualityFields {
  torsion_id: string;
  atoms_in_fragment: number[];
  /**
   * The canonical `FitFailReason` literal — a *narrowing* of what AFFDO
   * recorded. A `no_improvement` verdict arrives here as `nonconvergence`,
   * which reads like a crash when in fact the fit converged and lost to
   * plain GAFF2. Show `affdo_quality` beside it.
   */
  fail_reason: string;
  fit_rmsd_kcal_per_mol: number | null;
}

/**
 * The `fit_status` values that mean "the force field kept standard GAFF2".
 *
 * Each is a recorded DECISION by AFFDO: the pre-fit gate found GAFF2 already
 * sufficient (`gaff_sufficient`), or the fit ran and was rejected in favour of
 * GAFF2 (`affdo_worse_than_gaff`, `no_improvement`).
 */
const GAFF_RETAINED_STATUSES: ReadonlySet<string> = new Set([
  "gaff_sufficient",
  "affdo_worse_than_gaff",
  "no_improvement",
]);

/**
 * True only when the artifact POSITIVELY says GAFF2 was retained (#869/#878).
 *
 * **Never infer this from the absence of `fitted_relative_kcal_per_mol`.** The
 * curve is also missing when the artifact predates the fit-profile block, when
 * the driver could not read `energies.json`, when the runner's length-mismatch
 * fallback dropped the block, and when the route could not join the overlay.
 * Reading absence as a decision turns every one of those gaps into the claim
 * "this torsion's refitted parameters were discarded" — about torsions whose
 * refitted parameters are in fact in the shipped force field.
 *
 * `fit_status` is unset on artifacts written before it existed, and that
 * correctly yields `false` here: unknown is not a decision.
 */
export function isGaffRetained(profile: DihedralProfile): boolean {
  const status = profile.fit_status;
  return typeof status === "string" && GAFF_RETAINED_STATUSES.has(status);
}

/**
 * Plain-language rendering of `affdo_quality`, for the panel caption.
 *
 * Only the values AFFDO actually writes are named; anything else falls
 * through to the raw token rather than being dropped, so a vocabulary added
 * upstream shows up as itself instead of disappearing from the UI.
 *
 * **`refit` is not optional decoration.** `high` / `medium` are the only two
 * band words that are a judgement about a fit rather than an outcome, and this
 * function used to render them as *"high-quality fit"* on every row — including
 * a row whose own panel says **Not refit**, which put "high-quality fit" and
 * "standard GAFF2 retained" on one torsion (flowstack#1223 B). The field's own
 * doc above already said not to: *read it beside `fit_status`, never label it
 * "fit quality" unconditionally*. The caller supplies that context, because the
 * caller is the one holding `fit_status` / the fitted-vs-failed row.
 *
 * On a not-refit row the band is reported as the RECORDED band and nothing
 * more. That is the whole of what can be said: a band word is stamped by
 * `classify_fit_quality`, and on a torsion whose parameters were not adopted it
 * describes a fit that is not in the force field.
 *
 * **`refit` is REQUIRED, and it used to default to `true`** (flowstack#1226
 * finding 1). `true` is the permissive value — it is what turns a `high` band
 * into the flat sentence *"high-quality fit"*, i.e. the claim that these
 * parameters are in the shipped force field — so defaulting to it meant a
 * caller that forgot the argument silently made the stronger claim, which is
 * the wrong way round for a tool whose whole subject is auditability. There is
 * no safe default in the other direction either: `false` would understate a
 * genuinely adopted fit. Both directions are claims, so the parameter is
 * mandatory and every caller states which one it is making. The caller always
 * knows — it is holding `fit_status` / the fitted-vs-failed row.
 */
export function describeAffdoQuality(
  quality: AffdoQuality,
  options: {
    /** `false` on a torsion the panel is reporting as NOT refit. No default. */
    refit: boolean;
  }
): string | null {
  const { refit } = options;
  switch (quality) {
    case null:
      return null;
    case "not_needed":
      return "GAFF2 already within tolerance — not refit";
    case "no_improvement":
      return "fit converged but did not beat GAFF2";
    case "poor":
      return "fit quality below the acceptance band";
    case "high":
      return refit ? "high-quality fit" : "AFFDO band: high — recorded for a fit that was not kept";
    case "medium":
      return refit
        ? "medium-quality fit"
        : "AFFDO band: medium — recorded for a fit that was not kept";
    case "none":
    case "unknown":
      return null;
    default:
      return quality;
  }
}

/**
 * Plain-language rendering of a `FailedTorsionRow.fail_reason`.
 *
 * The raw token stays on screen beside this — it is a closed vocabulary
 * compared character-for-character against the API and the tests — but the
 * token alone reads as a claim about the number next to it, and for
 * `fit_rmsd_above_threshold` that reading is wrong: **AFFDO's acceptance gates
 * read the dimensionless `norm_RMSE`, not the kcal/mol RMSD** (see
 * `docs/source/dev/reference/torsion-fit-cascade.rst`, "The two quantities").
 *
 * `null` for a token this release does not name, so an upstream addition shows
 * up as its raw self rather than as a wrong sentence.
 */
export function describeFailReason(reason: string): string | null {
  switch (reason) {
    case "fit_rmsd_above_threshold":
      return "the fit's norm_RMSE missed AFFDO's acceptance band";
    case "nonconvergence":
      // The API narrows AFFDO's `no_improvement` onto this literal — see
      // `FailedTorsionRow.fail_reason`. "Did not converge" alone would report a
      // crash for a fit that converged and simply lost to the baseline.
      //
      // "the standard parameters", not "GAFF2": the base force field is a value
      // the artifact declares (`mm_initial_force_field`), and this function has
      // no artifact — naming GAFF2 here would be the unbacked claim the panel's
      // own retained-force-field note exists to avoid.
      return "the fit did not converge, or converged without improving on the standard parameters";
    case "ill_conditioned":
      return "the fit was ill-conditioned";
    case "manually_excluded":
      return "excluded before fitting";
    default:
      return null;
  }
}

/**
 * Decimal places for EVERY fit-RMSD figure in the app (kcal/mol).
 *
 * One number, in one place, because the same metric was being printed to three
 * places in the tiles and the panel headers and to two on the failed panel's
 * status line — so `1.900` and `1.90` were the same measurement wearing two
 * faces, and a reader comparing them had to work out that nothing had changed.
 *
 * **Three, not two**, for the reason O0.1 recorded when under-precision printed
 * a real signal as `0.00000`: the acceptance band is 1.0 kcal/mol and fits
 * cluster well under it, so the third decimal is where two adequate fits
 * actually differ. Rounding it away costs information; carrying it costs a
 * character.
 */
export const RMSD_DECIMALS = 3;

/** A fit RMSD in kcal/mol at the app's one precision — see `RMSD_DECIMALS`. */
export function formatRmsdKcal(value: number): string {
  return value.toFixed(RMSD_DECIMALS);
}

export interface FragmentDihedralsResponse {
  torsion_refinement_result_id: string;
  fragment_id: string;
  fitted: FittedTorsionRow[];
  failed: FailedTorsionRow[];
}

/**
 * One torsion's profile, ready to plot.
 *
 * Every series is **relative** — min-subtracted, never an absolute energy.
 * A QM total energy and a GAFF2 evaluation share no zero (roughly -6e4 vs
 * -6e2 kcal/mol), so the raw magnitudes cannot go on one axis; the names say
 * `relative` so nobody re-derives that the hard way.
 */
export interface DihedralProfile extends Partial<TorsionQualityFields> {
  torsion_id: string;
  angles_deg: number[];
  qm_relative_kcal_per_mol: number[];
  mm_initial_relative_kcal_per_mol: number[];
  /** The floors the server subtracted, so the absolute scale stays auditable.
   * Not plotted, and not currently read by the chart — they exist for API
   * consumers. See `docs/source/dev/reference/affdo-quality-projection.rst`,
   * which is the authoritative statement; do not restate it here.
   *
   * Both are `null` on the FIT-overlay path: there the runner min-subtracted
   * upstream and the floors it removed are not in the artifact, so the server
   * reports None rather than inventing a 0.0. */
  qm_zero_kcal_per_mol?: number | null;
  mm_initial_zero_kcal_per_mol?: number | null;
  /** The force field `mm_profile_initial` was evaluated with, verbatim from
   * the artifact. The dashed series is labelled from THIS, never from a
   * hard-coded "GAFF2" — the base force field is a value the artifact
   * declares, and asserting it in the presentation layer is the same unbacked
   * claim about a stored quantity that the unit handling exists to prevent. */
  mm_initial_force_field?: string | null;
  /**
   * How many of the scan's grid points the optimisation did not converge at,
   * verbatim from the record (#1231).
   *
   * **Never caption this as a count of INTERPOLATED points.** The producer
   * counts a point when it is `not converged` OR its `energy_source` is
   * `'interpolated'`, so the meaning turns on the `unconverged_handling` policy
   * the scan ran under. Under the default `'interpolate'` this is exactly the
   * set wearing the amber dashed ring; under `'include'`, and on the
   * XTB-passthrough branch, it is positive while EVERY geometry is flagged
   * `optimized` and no ring is drawn. The server's `DihedralProfile` note
   * carries the evidence; do not restate it here.
   *
   * So the only claim this licenses is *the optimisation did not settle at N
   * points*. "Which points are interpolated" is answered per-point by
   * `geometry_source` on the scan-geometry route, which is what
   * `scanPointMarks` reads and what the ring is drawn from.
   *
   * `null` on a `wavefront` record and on a blob that does not carry a
   * non-negative integer here.
   *
   * It describes the SCAN's grid. On the fit-overlay path `angles_deg` is the
   * FIT's grid, so this must not be rendered as a fraction of the plotted
   * points unless the caller has established the two grids are the same —
   * which, on the chart, is exactly what supplying `scanMarks` means.
   */
  n_unconverged_points?: number | null;
  /**
   * The post-fit MM profile — the third curve, and the one that answers
   * "did the fit actually beat GAFF?" (#869).
   *
   * `null` for a torsion that was never fitted (AFFDO's pre-fit gate found
   * standard GAFF2 already sufficient) or whose fit was REJECTED. In both
   * cases standard GAFF2 is what ends up in the force field, and the panel
   * says so rather than rendering blank.
   *
   * When present, the server has already replaced `angles_deg` and the two
   * lists above with the grid the FIT ran on — the post-drop, renumbered
   * one — so all four arrays are index-aligned.
   */
  fitted_relative_kcal_per_mol?: number[] | null;
  /** kcal/mol, and a DIFFERENT quantity from `fit_norm_rmse` — not a
   * rescaling of it. Present only on the fit-overlay path. */
  fit_rmsd_kcal_per_mol?: number | null;
  /** `fitted` | `gaff_sufficient` | `affdo_worse_than_gaff` | `no_improvement`.
   *
   * This is the ONLY field that licenses a "standard GAFF2 retained" claim.
   * Absence of `fitted_relative_kcal_per_mol` does not — see `isGaffRetained`. */
  fit_status?: string | null;
}

export interface FragmentProfilesResponse {
  fragment_id: string;
  profiles: DihedralProfile[];
}

export interface SkippedDihedralRow {
  fragment_id: string;
  torsion_id: string;
  atoms_in_fragment: number[];
  fail_reason: string;
  fit_rmsd_kcal_per_mol: number | null;
}

export interface SkippedDihedralsResponse {
  run_id: string;
  skipped: SkippedDihedralRow[];
}

export interface MethodRecommendationRequest {
  n_heavy_atoms: number;
  formal_charge: number;
  has_aromatic_ring: boolean;
  available_plugins: string[];
}

export interface MethodAlternative {
  charge_model: string;
  base_forcefield: string;
  qm_method: string | null;
  rationale: string;
}

export interface MethodRecommendationResponse {
  charge_model: string;
  base_forcefield: string;
  qm_method: string | null;
  confidence: number;
  rationale: string;
  alternatives: MethodAlternative[];
}

/**
 * `refreshKey` — an optional PROGRESS fingerprint appended to the key.
 *
 * These projections are computed from whatever artifacts exist at request
 * time, so on a live run they go stale the moment a new `torsion_fit` child
 * lands. Giving them their own `refetchInterval` would be the second polling
 * loop flowstack#950 rules out; folding the run view's existing DAG-poll
 * fingerprint into the key refreshes them on the poll that is already
 * happening (Rupert PR-955 r0). Omitted by a caller that wants the plain
 * cached read, in which case the key is exactly what it was before.
 */
export function runFragmentsQueryKey(runId: string, refreshKey?: string): readonly unknown[] {
  return ["runs", runId, "affdo", "fragments", refreshKey ?? null] as const;
}

export function fragmentDihedralsQueryKey(
  runId: string,
  fragmentId: string,
  refreshKey?: string
): readonly unknown[] {
  return [
    "runs",
    runId,
    "affdo",
    "fragments",
    fragmentId,
    "dihedrals",
    refreshKey ?? null,
  ] as const;
}

export function fragmentProfilesQueryKey(
  runId: string,
  fragmentId: string,
  refreshKey?: string
): readonly unknown[] {
  return ["runs", runId, "affdo", "fragments", fragmentId, "profiles", refreshKey ?? null] as const;
}

/** Per-fragment summary for a run. ``enabled`` defaults to true when
 * ``runId`` is non-empty so callers can omit the options bag for
 * the canonical case. */
export function useRunFragments(
  runId: string,
  options?: { enabled?: boolean; refreshKey?: string }
) {
  const enabled = (options?.enabled ?? true) && Boolean(runId);
  return useQuery({
    queryKey: runFragmentsQueryKey(runId, options?.refreshKey),
    placeholderData: (previous) => previous,
    queryFn: async (): Promise<FragmentSummaryListResponse> =>
      apiFetch<FragmentSummaryListResponse>(
        `/api/runs/${encodeURIComponent(runId)}/affdo/fragments`
      ),
    enabled,
    staleTime: 30_000,
  });
}

/** Per-torsion table for one fragment of one run. */
export function useFragmentDihedrals(
  runId: string,
  fragmentId: string,
  options?: { enabled?: boolean; refreshKey?: string }
) {
  const enabled = (options?.enabled ?? true) && Boolean(runId) && Boolean(fragmentId);
  return useQuery({
    queryKey: fragmentDihedralsQueryKey(runId, fragmentId, options?.refreshKey),
    placeholderData: (previous) => previous,
    queryFn: async (): Promise<FragmentDihedralsResponse> =>
      apiFetch<FragmentDihedralsResponse>(
        `/api/runs/${encodeURIComponent(runId)}/affdo/fragments/${encodeURIComponent(fragmentId)}/dihedrals`
      ),
    enabled,
    staleTime: 30_000,
  });
}

export function runSkippedQueryKey(runId: string): readonly unknown[] {
  return ["runs", runId, "affdo", "skipped"] as const;
}

/** Aggregated skipped-dihedrals list for the run. The
 * SkippedDihedralsList consumes this; the per-fragment small-multiples
 * already surface ghost panels inline. */
export function useRunSkipped(runId: string, options?: { enabled?: boolean }) {
  const enabled = (options?.enabled ?? true) && Boolean(runId);
  return useQuery({
    queryKey: runSkippedQueryKey(runId),
    queryFn: async (): Promise<SkippedDihedralsResponse> =>
      apiFetch<SkippedDihedralsResponse>(`/api/runs/${encodeURIComponent(runId)}/affdo/skipped`),
    enabled,
    staleTime: 30_000,
  });
}

/** Method-selection mutation. Returns a recommendation card the GUI
 * animates in with a confidence arc + 2-3 line rationale. */
export function useRecommendMethod() {
  return useMutation({
    mutationFn: async (body: MethodRecommendationRequest): Promise<MethodRecommendationResponse> =>
      apiFetch<MethodRecommendationResponse>("/api/affdo/recommend-method", {
        method: "POST",
        json: body,
      }),
  });
}

/** Per-dihedral QM + MM-initial scan profiles for one fragment. The
 * DihedralRMSDGrid joins this on torsion_id with useFragmentDihedrals
 * to render the QM solid + MM dashed curves alongside the RMSD bar. */
export function useFragmentProfiles(
  runId: string,
  fragmentId: string,
  options?: { enabled?: boolean; refreshKey?: string }
) {
  const enabled = (options?.enabled ?? true) && Boolean(runId) && Boolean(fragmentId);
  return useQuery({
    queryKey: fragmentProfilesQueryKey(runId, fragmentId, options?.refreshKey),
    placeholderData: (previous) => previous,
    queryFn: async (): Promise<FragmentProfilesResponse> =>
      apiFetch<FragmentProfilesResponse>(
        `/api/runs/${encodeURIComponent(runId)}/affdo/fragments/${encodeURIComponent(fragmentId)}/profiles`
      ),
    enabled,
    staleTime: 60_000,
  });
}
